import React, { useState, useEffect } from 'react';
import Input from '../../ui/Input';
import Button from '../../ui/Button';
import { useAuth } from '../../../hooks/useAuth';
import { useFirestore } from '../../../hooks/useFirestore';

interface CreateInstitutionProps {
  onSuccess: () => void;
  onCancel: () => void;
}

interface Country {
  id: string;
  currency: {
    code: string;
    name: string;
    symbol: string;
  };
  name?: string;
  code?: string;
}

const CreateInstitution: React.FC<CreateInstitutionProps> = ({
  onSuccess,
  onCancel,
}) => {
  const { user } = useAuth();
  const { addDocument } = useFirestore('institutions');
  const { data: countriesData, getDocuments: getCountries } = useFirestore<Country>('countries');
  
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState<Country[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    email: '',
    contactPerson: '',
    currency: 'XOF', // Devise par délaut
  });

  // Charger les pays et devises au montage
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true);
        await getCountries();
      } catch (err) {
        console.error('Erreur lors du chargement des pays:', err);
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, [getCountries]);

  // Mettre à jour la liste des pays quand les données arrivent
  useEffect(() => {
    if (countriesData) {
      const countryList = (countriesData as Country[]) || [];
      setCountries(countryList);
      
      // Si la liste est chargée et qu'on a pas encore défini une devise, on peut essayer de trouver XOF
      if (countryList.length > 0 && !formData.currency) {
        const defaultCurrency = countryList.find(c => 
          c.currency && c.currency.code === 'XOF'
        ) || countryList[0];
        
        if (defaultCurrency?.currency) {
          setFormData(prev => ({ ...prev, currency: defaultCurrency.currency.code }));
        }
      }
    }
  }, [countriesData]);

  // Obtenir toutes les devises uniques
  const getUniqueCurrencies = () => {
    const currenciesSet = new Set<string>();
    const currenciesList: Array<{
      code: string;
      symbol: string;
      name: string;
      countries: string[]; // Liste des pays qui utilisent cette devise
    }> = [];
    
    countries.forEach(country => {
      if (country.currency && country.currency.code && !currenciesSet.has(country.currency.code)) {
        currenciesSet.add(country.currency.code);
        currenciesList.push({
          code: country.currency.code,
          symbol: country.currency.symbol || country.currency.code,
          name: country.currency.name || country.currency.code,
          countries: [country.name || country.code || 'Inconnu']
        });
      } else if (country.currency && country.currency.code) {
        // Ajouter le pays à la liste des pays utilisant cette devise
        const existingCurrency = currenciesList.find(c => c.code === country.currency.code);
        if (existingCurrency && !existingCurrency.countries.includes(country.name || country.code || 'Inconnu')) {
          existingCurrency.countries.push(country.name || country.code || 'Inconnu');
        }
      }
    });
    
    // Trier par nom de devise
    return currenciesList.sort((a, b) => a.name.localeCompare(b.name));
  };

  // Obtenir la devise actuellement sélectionnée
  const getCurrentCurrency = () => {
    const currencies = getUniqueCurrencies();
    return currencies.find(c => c.code === formData.currency) || 
           currencies.find(c => c.code === 'XOF') || 
           currencies[0] || 
           { code: 'XOF', symbol: 'CFA', name: 'Franc CFA', countries: [] };
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    // Validation des champs obligatoires
    const requiredFields = ['name', 'address', 'city', 'email', 'currency'];
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    
    if (missingFields.length > 0) {
      setError('Veuillez remplir tous les champs obligatoires (*)');
      return false;
    }

    // Validation email
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Email invalide');
      return false;
    }

    // Validation téléphone (optionnel mais si fourni, doit être valide)
    if (formData.phone && !/^[+]?[\d\s\-()]+$/.test(formData.phone.replace(/\s/g, ''))) {
      setError('Numéro de téléphone invalide');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Récupérer les informations complètes de la devise sélectionnée
      const selectedCurrency = getCurrentCurrency();
      const currencyData = selectedCurrency ? {
        code: selectedCurrency.code,
        symbol: selectedCurrency.symbol,
        name: selectedCurrency.name
      } : {
        code: 'XOF',
        symbol: 'CFA',
        name: 'Franc CFA'
      };

      // Générer un ID unique pour l'institution
      const institutionId = `INST${Date.now().toString().slice(-8)}`;

      // Créer l'institution avec les données de devise
      await addDocument({
        ...formData,
        institutionId,
        currency: currencyData, // Stocker toutes les infos de la devise
        createdBy: user?.uid,
        createdByEmail: user?.email,
        createdByName: user?.displayName,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        balance: 0,
        totalRevenue: 0,
        totalExpenses: 0,
        // Champs pour statistiques
        studentCount: 0,
        secretaryCount: 0,
        transactionCount: 0,
        // Métadonnées
        isActive: true,
        lastActivity: new Date(),
      });

      // Ajout d'un délai pour laisser Firestore mettre à jour les données
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Appeler onSuccess qui fermera le modal
      onSuccess();
      
      // Ajouter un événement personnalisé pour indiquer qu'une nouvelle institution a été créée
      window.dispatchEvent(new CustomEvent('institution-created'));
      
    } catch (err) {
      console.error('Erreur lors de la création de l\'institution:', err);
      setError('Une erreur est survenue lors de la création. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const currencies = getUniqueCurrencies();
  const currentCurrency = getCurrentCurrency();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div>
        <Input
          label="Nom de l'institution *"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          disabled={isLoading || loadingCountries}
          placeholder="Ex: Association des Femmes Entrepreneures"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Ville *"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
            disabled={isLoading || loadingCountries}
            placeholder="Ex: Abidjan"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Devise * {loadingCountries && <span className="text-gray-400 text-xs ml-2">(Chargement...)</span>}
          </label>
          <select
            name="currency"
            value={formData.currency}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            required
            disabled={isLoading || loadingCountries}
          >
            <option value="">Sélectionnez une devise</option>
            {currencies.map((currency) => (
              <option key={currency.code} value={currency.code}>
                {currency.name} ({currency.symbol}) - {currency.code}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Adresse complète *
        </label>
        <textarea
          name="address"
          value={formData.address}
          onChange={handleChange}
          required
          disabled={isLoading || loadingCountries}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          placeholder="Adresse postale complète"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Input
            label="Email *"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={isLoading || loadingCountries}
            placeholder="contact@institution.com"
          />
        </div>
        <div>
          <Input
            label="Téléphone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            disabled={isLoading || loadingCountries}
            placeholder="+225 01 23 45 67 89"
          />
        </div>
      </div>

      <div>
        <Input
          label="Personne de contact"
          name="contactPerson"
          value={formData.contactPerson}
          onChange={handleChange}
          disabled={isLoading || loadingCountries}
          placeholder="Nom de la personne de contact"
        />
      </div>

      {/* Information sur la devise sélectionnée */}
      {formData.currency && currentCurrency && (
        <div className={`p-4 rounded-lg border ${
          currentCurrency.code === 'XOF' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-start">
            <div className="flex-1">
              <h4 className="font-medium text-gray-900">Devise sélectionnée</h4>
              <div className="mt-1 space-y-1">
                <div className="flex items-center">
                  <span className="text-lg font-bold mr-2">{currentCurrency.symbol}</span>
                  <span className="text-gray-700">{currentCurrency.name}</span>
                  <span className="ml-2 text-sm text-gray-500">({currentCurrency.code})</span>
                </div>
                {currentCurrency.countries.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Pays utilisant cette devise :</span>{' '}
                    {currentCurrency.countries.slice(0, 3).join(', ')}
                    {currentCurrency.countries.length > 3 && '...'}
                  </div>
                )}
              </div>
            </div>
            {currentCurrency.code === 'XOF' && (
              <div className="ml-4">
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                  Défaut
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Information importante */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-2">Information importante</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong>Devise :</strong> Une fois définie, la devise de l'institution ne peut être modifiée que par un administrateur.</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong>Identifiant :</strong> Un identifiant unique sera généré automatiquement pour cette institution.</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span><strong>Secrétaires :</strong> Les secrétaires devront utiliser cet identifiant pour s'inscrire et accéder au système.</span>
          </li>
        </ul>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading || loadingCountries}
          className="disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="primary"
          isLoading={isLoading || loadingCountries}
          disabled={loadingCountries}
        >
          {loadingCountries ? 'Chargement des devises...' : 'Créer l\'institution'}
        </Button>
      </div>
    </form>
  );
};

export default CreateInstitution;